import {
    Emoji,
    EmojiText,
} from '@gagovictor/shared-frontend-core/dist/shared/components/Emoji';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
    Box,
    Button,
    Drawer,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { useDispatch, useSelector } from 'react-redux';

import { MessageKey, useT } from '../../i18n';
import { RootState } from '../../store';
import {
    BACKGROUNDS,
    COMPLETIONS,
    CompletionId,
    EYES,
    HATS,
    MODELS,
    MOUTHS,
    NECKLACES,
    Option,
    SWATCHES,
    VINES,
} from '../catalog';
import { playCue } from '../feedback';
import { Goal, GOALS, lockingGoal, unlockedLook } from '../goals';
import { setCompletion, setLook } from '../tomatoSlice';

import { GoalMeter } from './GoalMeter';

export const DRAWER_WIDTH = 380;
export const SHEET_HEIGHT_VH = 48;
export const FOOTER_HEIGHT = { xs: 60, sm: 90 };

interface Props {
    open: boolean;
    onClose: () => void;
    onPreview: (kind: CompletionId) => void;
    previewing: boolean;
    footerOffset: boolean;
}

/** A grid of big tappable choices. */
function Choices<Id extends string>({
    options,
    value,
    onPick,
    label,
    group,
}: {
    options: Option<Id>[];
    value: Id;
    onPick: (id: Id) => void;
    label: string;
    /** Prefix of the option labels in the message catalog, e.g. "eyes" for `opt.eyes.googly`. */
    group: string;
}) {
    const t = useT();
    const goalGroup = (group === 'bg' ? 'background' : group) as Goal['group'];
    return (
        <Box
            role="radiogroup"
            aria-label={label}
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
                gap: 1,
            }}
        >
            {options.map((o) => {
                const selected = o.id === value;
                const locked = lockingGoal(goalGroup, o.id) !== undefined;
                return (
                    <Button
                        key={o.id}
                        role="radio"
                        aria-checked={selected}
                        disabled={locked}
                        title={locked ? t('goal.locked') : undefined}
                        onClick={() => onPick(o.id)}
                        variant={selected ? 'contained' : 'outlined'}
                        sx={{
                            flexDirection: 'column',
                            height: 76,
                            borderRadius: 3,
                            lineHeight: 1.1,
                            gap: 0.25,
                        }}
                    >
                        <Emoji emoji={locked ? '🔒' : o.emoji} size={30} />
                        <Typography variant="caption" sx={{ fontWeight: 800 }}>
                            {optionLabel(t, group, o.id)}
                        </Typography>
                    </Button>
                );
            })}
        </Box>
    );
}

const optionLabel = (
    t: (key: MessageKey) => string,
    group: string,
    id: string
): string =>
    t((id === 'none' ? 'opt.none' : `opt.${group}.${id}`) as MessageKey);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
}) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 800, opacity: 0.8 }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

export const CustomizeDrawer: React.FC<Props> = ({
    open,
    onClose,
    onPreview,
    previewing,
    footerOffset,
}) => {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const t = useT();
    const { look: savedLook, completion } = useSelector(
        (s: RootState) => s.tomato
    );
    const look = unlockedLook(savedLook);
    const [tab, setTab] = useState(0);

    const pick =
        <K extends keyof typeof look>(key: K) =>
        (id: (typeof look)[K]) => {
            playCue('equip', 0.9 + Math.random() * 0.4);
            dispatch(setLook({ [key]: id }));
        };

    return (
        <Drawer
            anchor={mobile ? 'bottom' : 'right'}
            variant="persistent"
            open={open}
            PaperProps={{
                sx: {
                    width: mobile ? '100%' : DRAWER_WIDTH,
                    height: mobile ? `${SHEET_HEIGHT_VH}vh` : 'auto',
                    top: mobile ? 'auto' : 0,
                    bottom: footerOffset
                        ? { xs: FOOTER_HEIGHT.xs, sm: FOOTER_HEIGHT.sm }
                        : 0,
                    borderTopLeftRadius: mobile ? 28 : 0,
                    borderTopRightRadius: mobile ? 28 : 0,
                    bgcolor: alpha(theme.palette.background.paper, 0.96),
                    backdropFilter: 'blur(14px)',
                    zIndex: theme.zIndex.drawer + 1,
                },
            }}
        >
            <Stack direction="row" alignItems="center" sx={{ px: 2, pt: 1 }}>
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 800 }}>
                    {t('drawer.title')}
                </Typography>
                <IconButton onClick={onClose} aria-label={t('drawer.close')}>
                    <CloseRoundedIcon />
                </IconButton>
            </Stack>
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 1 }}
            >
                <Tab label={<EmojiText>{t('drawer.tab.model')}</EmojiText>} />
                <Tab label={<EmojiText>{t('drawer.tab.color')}</EmojiText>} />
                <Tab label={<EmojiText>{t('drawer.tab.face')}</EmojiText>} />
                <Tab label={<EmojiText>{t('drawer.tab.vines')}</EmojiText>} />
                <Tab label={<EmojiText>{t('drawer.tab.scene')}</EmojiText>} />
                <Tab label={<EmojiText>{t('drawer.tab.finish')}</EmojiText>} />
            </Tabs>

            <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
                {tab === 0 && (
                    <Section title={t('drawer.model')}>
                        <Choices
                            label={t('drawer.model')}
                            group="model"
                            options={MODELS}
                            value={look.model}
                            onPick={pick('model')}
                        />
                    </Section>
                )}

                {tab === 1 && (
                    <Stack spacing={2} alignItems="center">
                        <HexColorPicker
                            color={look.color}
                            onChange={(c) => dispatch(setLook({ color: c }))}
                            style={{
                                width: '100%',
                                height: mobile ? 130 : 200,
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                            }}
                        >
                            {SWATCHES.map((c) => (
                                <Box
                                    key={c}
                                    component="button"
                                    aria-label={t('drawer.useColor', {
                                        color: c,
                                    })}
                                    onClick={() => {
                                        playCue('equip', 1.2);
                                        dispatch(setLook({ color: c }));
                                    }}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        bgcolor: c,
                                        cursor: 'pointer',
                                        border: '3px solid',
                                        borderColor:
                                            c.toLowerCase() ===
                                            look.color.toLowerCase()
                                                ? 'text.primary'
                                                : 'transparent',
                                        boxShadow: 2,
                                        transition: 'transform .1s',
                                        '&:active': { transform: 'scale(.88)' },
                                    }}
                                />
                            ))}
                        </Box>
                        <Box
                            sx={{
                                '& input': {
                                    width: 120,
                                    textAlign: 'center',
                                    padding: '10px',
                                    borderRadius: 12,
                                    border: '2px solid',
                                    borderColor: 'divider',
                                    background: 'transparent',
                                    color: 'inherit',
                                    fontSize: 16,
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                },
                            }}
                        >
                            <HexColorInput
                                color={look.color}
                                onChange={(c) =>
                                    dispatch(setLook({ color: c }))
                                }
                                prefixed
                                aria-label={t('drawer.hex')}
                            />
                        </Box>
                    </Stack>
                )}

                {tab === 2 && (
                    <>
                        <Section title={t('drawer.eyes')}>
                            <Choices
                                label={t('drawer.eyes')}
                                group="eyes"
                                options={EYES}
                                value={look.eyes}
                                onPick={pick('eyes')}
                            />
                        </Section>
                        <Section title={t('drawer.mouth')}>
                            <Choices
                                label={t('drawer.mouth')}
                                group="mouth"
                                options={MOUTHS}
                                value={look.mouth}
                                onPick={pick('mouth')}
                            />
                        </Section>
                        <Section title={t('drawer.hat')}>
                            <Choices
                                label={t('drawer.hat')}
                                group="hat"
                                options={HATS}
                                value={look.hat}
                                onPick={pick('hat')}
                            />
                            {GOALS.filter((g) => g.group === 'hat').map((g) => (
                                <GoalMeter key={g.id} goal={g} />
                            ))}
                        </Section>
                        <Section title={t('drawer.necklace')}>
                            <Choices
                                label={t('drawer.necklace')}
                                group="neck"
                                options={NECKLACES}
                                value={look.neck}
                                onPick={pick('neck')}
                            />
                        </Section>
                    </>
                )}

                {tab === 3 && (
                    <>
                        <Section title={t('drawer.vine')}>
                            <Choices
                                label={t('drawer.vine')}
                                group="vine"
                                options={VINES}
                                value={look.vine}
                                onPick={pick('vine')}
                            />
                        </Section>
                    </>
                )}

                {tab === 4 && (
                    <Section title={t('drawer.scene')}>
                        <Choices
                            label={t('drawer.scene')}
                            group="bg"
                            options={BACKGROUNDS}
                            value={look.background}
                            onPick={pick('background')}
                        />
                        {look.background === 'default' && (
                            <Stack spacing={1.5} alignItems="center" mt={2}>
                                <HexColorPicker
                                    color={look.glowColor || look.color}
                                    onChange={(c) =>
                                        dispatch(setLook({ glowColor: c }))
                                    }
                                    aria-label={t('drawer.glowColor')}
                                    style={{
                                        width: '100%',
                                        height: mobile ? 110 : 160,
                                    }}
                                />
                                <Button
                                    size="small"
                                    disabled={!look.glowColor}
                                    onClick={() =>
                                        dispatch(setLook({ glowColor: '' }))
                                    }
                                >
                                    {t('drawer.glowMatch')}
                                </Button>
                            </Stack>
                        )}
                        {GOALS.filter((g) => g.group === 'background').map(
                            (g) => (
                                <GoalMeter key={g.id} goal={g} />
                            )
                        )}
                    </Section>
                )}

                {tab === 5 && (
                    <Section title={t('drawer.whenEnds')}>
                        <Stack spacing={1}>
                            {COMPLETIONS.map((c) => (
                                <Stack
                                    key={c.id}
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <Button
                                        fullWidth
                                        role="radio"
                                        aria-checked={completion === c.id}
                                        variant={
                                            completion === c.id
                                                ? 'contained'
                                                : 'outlined'
                                        }
                                        onClick={() => {
                                            playCue('equip');
                                            dispatch(setCompletion(c.id));
                                        }}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            borderRadius: 3,
                                            textAlign: 'left',
                                            py: 1,
                                        }}
                                    >
                                        <Emoji
                                            emoji={c.emoji}
                                            size={32}
                                            style={{ marginRight: 12 }}
                                        />
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                {t(
                                                    `opt.done.${c.id}` as MessageKey
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ opacity: 0.85 }}
                                            >
                                                {t(
                                                    `opt.done.${c.id}.blurb` as MessageKey
                                                )}
                                            </Typography>
                                        </Box>
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        disabled={previewing}
                                        onClick={() => onPreview(c.id)}
                                        aria-label={t('drawer.preview', {
                                            name: t(
                                                `opt.done.${c.id}` as MessageKey
                                            ),
                                        })}
                                    >
                                        {t('drawer.try')}
                                    </Button>
                                </Stack>
                            ))}
                        </Stack>
                    </Section>
                )}
            </Box>
        </Drawer>
    );
};
